import { useEffect, useState } from 'react';

import { Icon } from '../Icon/Icon';
import { IconNames } from '../Icon/IconNames';
import { Text } from '../Text/Text';
import { ButtonLink } from '../Button/ButtonLink';
import { APP_STORE_URL, GOOGLE_PLAY_URL, isDemo } from '../../constants/common';
import { useAppSelector } from '../../hooks';
import { t } from '../../i18n';
import localStorage from '../../services/localStorage/localStorage';
import { selectSecurity } from '../../store/securitySlice';

type MobilePlatform = 'ios' | 'android';

/** Ikona aplikace — stejný soubor jako apple-touch-icon v public/favicon. */
const APP_ICON_SRC = '/favicon/apple-touch-icon.png';

/** Po zavření lištu neukazujeme 30 dní. */
const DISMISS_DAYS = 30;

/** Stejný breakpoint jako Sidebar / hamburger ($screen-lg = 992px). */
const MOBILE_MEDIA_QUERY = '(max-width: 991px)';

const STORE_URLS: Record<MobilePlatform, string> = {
  ios: APP_STORE_URL,
  android: GOOGLE_PLAY_URL,
};

/** Logo storu v tlačítku (monochromatické glyfy ze Simple Icons, CC0) — ať je na první pohled jasné, kam odkaz vede. */
const STORE_ICONS: Record<MobilePlatform, IconNames> = {
  ios: 'appStore',
  android: 'googlePlay',
};

const detectPlatform = (): MobilePlatform | null => {
  const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  if (/iPhone|iPad|iPod/.test(userAgent)) return 'ios';
  if (/Android/.test(userAgent)) return 'android';
  return null;
};

const isDismissed = (): boolean => {
  const dismissedAt = localStorage.getMobileAppBannerDismissedAt();
  if (!dismissedAt) return false;

  return Date.now() - dismissedAt < DISMISS_DAYS * 24 * 60 * 60 * 1000;
};

const useIsMobileViewport = (): boolean => {
  const [isMobile, setIsMobile] = useState(() => window.matchMedia(MOBILE_MEDIA_QUERY).matches);

  useEffect(() => {
    const mediaQuery = window.matchMedia(MOBILE_MEDIA_QUERY);
    const handleChange = (event: MediaQueryListEvent) => setIsMobile(event.matches);

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  return isMobile;
};

/**
 * Lišta propagující mobilní aplikaci (náhrada Apple Smart App Banneru z index.html, který
 * fungoval jen v Safari a ukazoval se i adminům). Mobilní aplikace je určená jen vlastníkům,
 * proto se lišta ukazuje pouze v členském režimu, mimo demo, na telefonu/tabletu s iOS nebo
 * Androidem (detekce z user agentu) a dokud ji uživatel nezavřel (zavření platí 30 dní).
 * Vykresluje se v toku stránky nad obsahem — na mobilu nad lepicí horní lištou s hamburgerem,
 * takže ji nic nepřekrývá a s obsahem odscrolluje pryč.
 */
export const MobileAppBanner: React.FC = () => {
  const uiState = useAppSelector(selectSecurity).ui.state;
  const isMobileViewport = useIsMobileViewport();
  const [platform] = useState<MobilePlatform | null>(detectPlatform);
  const [dismissed, setDismissed] = useState<boolean>(isDismissed);

  if (uiState !== 'member' || isDemo || !isMobileViewport || !platform || dismissed) return null;

  const handleDismiss = () => {
    localStorage.setMobileAppBannerDismissedAt(Date.now());
    setDismissed(true);
  };

  return (
    <div className="MobileAppBanner" role="complementary" aria-label={t('MobileAppBanner.title')}>
      <img src={APP_ICON_SRC} alt="" className="MobileAppBanner__icon" width={48} height={48} />

      <div className="MobileAppBanner__body">
        <Text as="div" size="body-medium" weight="600" color="Neutral800">
          {t('MobileAppBanner.title')}
        </Text>
        <Text as="div" size="body-small" weight="500" color="Neutral700">
          {t('MobileAppBanner.text')}
        </Text>
        <ButtonLink
          external
          to={STORE_URLS[platform]}
          target="_blank"
          rel="noopener"
          size="small"
          title={t(`MobileAppBanner.download.${platform}`)}
          iconBefore={STORE_ICONS[platform]}
          className="MobileAppBanner__button"
        />
      </div>

      <button
        type="button"
        className="MobileAppBanner__close"
        aria-label={t('MobileAppBanner.close')}
        onClick={handleDismiss}
      >
        <Icon name="times" />
      </button>
    </div>
  );
};
